import { Vector } from '@moleculeone/types';
import { debug, number } from '../checks';
import { infNum, pointTest } from './tests';

export class V4 {
  static zerovect: Vector = [0, 0, 0];
  static unitvect: Vector = [1, 1, 1];

  static posinfv: Vector = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];

  static neginfv: Vector = [Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY];

  static vectx: Vector = [1, 0, 0];
  static vecty: Vector = [0, 1, 0];
  static vectz: Vector = [0, 0, 1];

  static add(...vects: Vector[]): Vector {
    if (vects.length === 0) {
      return V4.zerovect;
    }
    let v1 = vects[0];
    for (let i = 1; i < arguments.length; i++) {
      const v2 = vects[i];
      v1 = [v1[0] + v2[0], v1[1] + v2[1], v1[2] + v2[2]];
    }
    pointTest(v1);
    return v1;
  }

  static sub(v1: Vector, v2: Vector): Vector {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return [v1[0] - v2[0], v1[1] - v2[1], v1[2] - v2[2]];
  }

  static dot(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return v1[0] * v2[0] + v1[1] * v2[1] + v1[2] * v2[2];
  }

  static dotX(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return v1[1] * v2[1] + v1[2] * v2[2];
  }

  static dotY(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return v1[0] * v2[0] + v1[2] * v2[2];
  }

  static dotZ(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return v1[0] * v2[0] + v1[1] * v2[1];
  }

  static mul(...vects: Vector[]): Vector {
    if (arguments.length === 0) {
      return V4.unitvect;
    }
    let v1 = vects[0];
    for (let i = 1; i < vects.length; i++) {
      const v2 = vects[i];
      v1 = [v1[0] * v2[0], v1[1] * v2[1], v1[2] * v2[2]];
    }
    pointTest(v1);
    return v1;
  }

  static max(...vects: Vector[]): Vector {
    if (arguments.length === 0) {
      return V4.neginfv;
    }
    let v1 = vects[0];
    for (let i = 1; i < vects.length; i++) {
      const v2 = vects[i];
      v1 = [Math.max(v1[0], v2[0]), Math.max(v1[1], v2[1]), Math.max(v1[2], v2[2])];
    }
    pointTest(v1);
    return v1;
  }

  static min(...vects: Vector[]): Vector {
    if (vects.length === 0) {
      return V4.posinfv;
    }
    let v1 = vects[0];
    for (let i = 1; i < vects.length; i++) {
      const v2 = vects[i];
      v1 = [Math.min(v1[0], v2[0]), Math.min(v1[1], v2[1]), Math.min(v1[2], v2[2])];
    }
    pointTest(v1);
    return v1;
  }

  static norm(v: Vector): number {
    debug(pointTest, v);

    return Math.sqrt(V4.dot(v, v));
  }

  static crossX(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return v1[1] * v2[2] - v1[2] * v2[1];
  }

  static crossY(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return v1[2] * v2[0] - v1[0] * v2[2];
  }

  static crossZ(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return v1[0] * v2[1] - v1[1] * v2[0];
  }

  static cross(v1: Vector, v2: Vector) {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return V4.vect(V4.crossX(v1, v2), V4.crossY(v1, v2), V4.crossZ(v1, v2));
  }

  static scale(v: Vector, c: number): Vector {
    debug(pointTest, v);
    debug(infNum, c);

    return [v[0] * c, v[1] * c, v[2] * c];
  }

  static normal(v: Vector, length = 1): Vector {
    debug(pointTest, v);
    debug(number, length);

    const norm = V4.norm(v);
    if (norm === 0) {
      return v;
    }
    return V4.scale(v, length / norm);
  }

  static affine(p1: Vector, a: number, p2: Vector): Vector {
    debug(pointTest, p1);
    debug(number, a);
    debug(pointTest, p2);

    return [p1[0] * (1 - a) + p2[0] * a, p1[1] * (1 - a) + p2[1] * a, p1[2] * (1 - a) + p2[2] * a];
  }

  static move(origin: Vector, direction: Vector, scale: number): Vector {
    debug(pointTest, origin);
    debug(pointTest, direction);
    debug(number, scale);

    return V4.add(origin, V4.scale(direction, scale));
  }

  static dist(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);

    return V4.norm(V4.sub(v2, v1));
  }

  static distsqr(v1: Vector, v2: Vector): number {
    debug(pointTest, v1);
    debug(pointTest, v2);
    const sub = V4.sub(v2, v1);
    return V4.dot(sub, sub);
  }

  static average(...vects: Vector[]): Vector {
    if (arguments.length === 0) {
      return V4.zerovect;
    }
    return V4.scale(V4.add.apply(null, vects), 1 / arguments.length);
  }

  static rotZ(v: Vector, angle: number): Vector {
    debug(pointTest, v);
    debug(number, angle);

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return [v[0] * cos - v[1] * sin, v[0] * sin + v[1] * cos, v[2]];
  }

  static project(vect: Vector, target: Vector): Vector {
    const dir = V4.normal(target);
    const factor = V4.dot(vect, dir);
    return V4.scale(dir, factor);
  }

  static segmentDist(point: Vector, segmentStart: Vector, segmentEnd: Vector): number {
    // don't try to read this code without drawing, either in your head or on paper.
    // first two substractions are moving the coordinate space so that segmentStart = (0, 0);
    const pointVect = V4.sub(point, segmentStart);
    const segmentVect = V4.sub(segmentEnd, segmentStart);
    const projectedPoint = V4.project(pointVect, segmentVect);
    const segmentCenter = V4.scale(segmentVect, 0.5);
    const centerDist = V4.dist(segmentCenter, projectedPoint);
    const segmentLength = V4.norm(segmentVect);
    const parallelDist = Math.max(0, centerDist - segmentLength / 2.0);
    const orthogonalDist = V4.dist(projectedPoint, pointVect);
    return Math.sqrt(parallelDist * parallelDist + orthogonalDist * orthogonalDist);
  }

  static pointsSegmentDist(s1: Vector, e1: Vector, s2: Vector, e2: Vector): number {
    return Math.min(
      V4.segmentDist(s1, s2, e2),
      V4.segmentDist(e1, s2, e2),
      V4.segmentDist(s2, s1, e1),
      V4.segmentDist(e2, s1, e1)
    );
  }

  static crossesLine(segmentStart: Vector, segmentEnd: Vector, lineStart: Vector, lineEnd: Vector): boolean {
    const lineVect = V4.sub(lineEnd, lineStart);
    const startRel = V4.sub(segmentStart, lineStart);
    const endRel = V4.sub(segmentEnd, lineEnd);
    const startProj = V4.project(startRel, lineVect);
    const endProj = V4.project(endRel, lineVect);
    return V4.dot(V4.sub(startRel, startProj), V4.sub(endRel, endProj)) < 0;
  }

  static segmentsCross(s1: Vector, e1: Vector, s2: Vector, e2: Vector): boolean {
    if (
      Math.max(s1[0], e1[0]) < Math.min(s2[0], e2[0]) ||
      Math.max(s2[0], e2[0]) < Math.min(s1[0], e1[0]) ||
      Math.max(s1[1], e1[1]) < Math.min(s2[1], e2[1]) ||
      Math.max(s2[1], e2[1]) < Math.min(s1[1], e1[1])
    ) {
      // segments are in separate boxes, so they cannot cross
      return false;
    }
    return V4.crossesLine(s1, e1, s2, e2) && V4.crossesLine(s2, e2, s1, e1);
  }

  static inConcave(point: Vector, polygon: Vector[]): boolean {
    let angle = 0;
    let lastvect = V4.sub(point, polygon[polygon.length - 1]);
    for (const nextp of polygon) {
      const nextvect = V4.sub(point, nextp);
      angle += V4.angleZ(nextvect, lastvect);
      lastvect = nextvect;
    }
    return Math.abs(angle) > Math.PI;
  }

  static angleZ(to: Vector, from: Vector): number {
    debug(pointTest, to);
    debug(pointTest, from);

    return Math.atan2(V4.crossZ(from, to), V4.dotZ(from, to));
  }

  static vect(x: number, y: number, z = 0): Vector {
    return [x, y, z];
  }

  static fromArray(arr: number[]): Vector {
    return [arr[0], arr[1], arr[2] || 0];
  }
}
